import React from "react";
import {
  BriefcaseBusiness,
  CalendarDays,
  Cloud,
  Euro,
  Files,
  LayoutGrid,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const MODULES = [
  {
    key: "studio",
    label: "Gestionale di Studio",
    shortLabel: "GI Studio",
    description: "Pratiche e attività dello studio",
    color: "#0B6B4F",
    icon: BriefcaseBusiness,
    localPort: 8001,
    publicUrl: "https://studio.gruppoimpresa.org",
    enabled: true,
  },
  {
    key: "presenze",
    label: "Gestionale Presenze",
    shortLabel: "GI Presenze",
    description: "Gestione presenze, assenze e banca ore",
    color: "#64AC50",
    icon: CalendarDays,
    localPort: 8002,
    publicUrl: "https://presenze.gruppoimpresa.org",
    enabled: true,
  },
  {
    key: "pagamenti",
    label: "Gestionale Incassi & Pagamenti",
    shortLabel: "GI Finance",
    description: "Incassi, pagamenti, scadenze e prima nota",
    color: "#5C9BC0",
    icon: Euro,
    localPort: 8003,
    publicUrl: "https://pagamenti.gruppoimpresa.org",
    enabled: true,
  },
  {
    key: "connect",
    label: "GI Connect",
    shortLabel: "GI Connect",
    description: "Portale online per cedolini, CU e comunicazioni",
    color: "#94A3B8",
    icon: Cloud,
    enabled: false,
  },
  {
    key: "documenti",
    label: "GI Documenti",
    shortLabel: "GI Documenti",
    description: "La memoria digitale dello Studio",
    color: "#94A3B8",
    icon: Files,
    enabled: false,
  },
];

function getModuleUrl(module) {
  if (typeof window === "undefined") return module.publicUrl;

  const host = window.location.hostname;
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "pc-paola2" ||
    /^192\.168\./.test(host) ||
    /^10\./.test(host);

  return isLocal
    ? `http://${host}:${module.localPort}`
    : module.publicUrl;
}

/**
 * Punto unico di passaggio fra le applicazioni della Gruppo Impresa Suite.
 * Replica il launcher del Gestionale Pagamenti e mantiene automaticamente
 * il contesto locale/rete oppure online.
 */
export function SuiteLauncher({ collapsed = false }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="suite-launcher-trigger"
          title="Applicazioni della Suite"
          className="flex h-9 w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-600 shadow-sm transition-colors hover:border-[var(--gis-green)]/40 hover:bg-emerald-50/40 hover:text-[var(--gis-green)]"
          aria-label="Applicazioni della Suite"
        >
          <LayoutGrid size={13} strokeWidth={1.9} className="shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Applicazioni</span>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                Suite
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        data-testid="suite-launcher-panel"
        className="w-[380px] max-w-[calc(100vw-24px)] p-0"
      >
        <div className="grid grid-cols-1 gap-1.5 p-2.5">
          {MODULES.map((module) => (
            <SuiteModuleTile
              key={module.key}
              module={module}
              current={module.key === "studio"}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SuiteModuleTile({ module, current }) {
  const Icon = module.icon;
  const disabled = !module.enabled;
  const content = (
    <>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          disabled
            ? "bg-slate-50 text-slate-300"
            : current
              ? "bg-emerald-50 text-[var(--gis-green)]"
              : "bg-slate-100 text-slate-600"
        }`}
      >
        <Icon size={17} strokeWidth={1.9} />
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`text-[11px] font-semibold ${
              disabled ? "text-slate-400" : "text-slate-800"
            }`}
          >
            {module.shortLabel}
          </span>
          {current && (
            <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-emerald-600">
              In uso
            </span>
          )}
          {disabled && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-slate-400">
              In preparazione
            </span>
          )}
        </div>
        <div
          className={`mt-1 whitespace-normal text-[9px] leading-4 ${
            disabled ? "text-slate-300" : "text-slate-500"
          }`}
        >
          {module.description}
        </div>
      </div>
    </>
  );

  if (disabled) {
    return (
      <div
        data-testid={`suite-tile-${module.key}`}
        className="flex cursor-default items-center gap-2.5 rounded-md border border-transparent bg-white p-2 opacity-75"
        title="Modulo in fase di realizzazione"
        aria-label={`${module.shortLabel}. Modulo in fase di realizzazione`}
      >
        {content}
      </div>
    );
  }

  if (current) {
    return (
      <div
        data-testid={`suite-tile-${module.key}`}
        className="flex items-center gap-2.5 rounded-md border border-transparent bg-white p-2"
        title="Stai lavorando in questa applicazione"
      >
        {content}
      </div>
    );
  }

  return (
    <a
      href={getModuleUrl(module)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`suite-tile-${module.key}`}
      title={`Apri ${module.label}`}
      className="flex items-center gap-2.5 rounded-md border border-transparent bg-white p-2 transition-colors hover:border-slate-200 hover:bg-slate-50"
    >
      {content}
    </a>
  );
}

export default SuiteLauncher;
